import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import ChatIcon from "@mui/icons-material/Chat";
import { useLocation } from "react-router-dom";
import { useChatRail } from "../features/agents/ChatRail/ChatRailProvider";
import { HelpDrawerContext, useHelpDrawer, type HelpDrawerCtx } from "./helpDrawerContext";

// ── Route → topic map ─────────────────────────────────────────────────────────

interface HelpTopic {
  title: string;
  sections: Array<{ heading: string; body: string }>;
}

const TOPICS: {
  contacts: HelpTopic;
  organizations: HelpTopic;
  deals: HelpTopic;
  admin: HelpTopic;
  agents: HelpTopic;
} = {
  contacts: {
    title: "Contacts",
    sections: [
      {
        heading: "Finding Contacts",
        body: "Use the Contacts list (g c) to browse, filter, and search. The lead score column ranks inbound prospects by fit and engagement — click the badge for a breakdown.",
      },
      {
        heading: "Creating a Contact",
        body: "Click Add contact or press n c. Fill in name and email; the rest can be added later.",
      },
      {
        heading: "Tags",
        body: 'Use Tags to label contacts (e.g. "champion", "cold", "decision-maker"). Tags are free-form text with no fixed taxonomy.',
      },
      {
        heading: "Merging Duplicates",
        body: 'Go to Admin → Duplicates → Contacts to review auto-detected duplicates and merge them. The merge keeps the "survivor" record and soft-deletes the loser.',
      },
      {
        heading: "Exporting",
        body: "Use the Export button on the Contacts list. For lists over 10 000 rows the export runs in the background — a notification appears when ready.",
      },
    ],
  },
  organizations: {
    title: "Organizations",
    sections: [
      {
        heading: "Hierarchy",
        body: "Set a parent organization to model subsidiaries or holding companies. The Hierarchy card on the detail page shows parent link, child org chips, and a rollup of deal count + pipeline revenue across the entire subtree.",
      },
      {
        heading: "Account Health",
        body: "The health score badge is computed by an agent skill. Click the badge for a factor breakdown.",
      },
      {
        heading: "Account Lists (ABM)",
        body: "Create named lists of target organizations under Marketing → Account Lists. Each list has an ABM dashboard with per-org activity metrics.",
      },
    ],
  },
  deals: {
    title: "Deals",
    sections: [
      {
        heading: "Kanban vs Table",
        body: "Switch between Kanban (drag-to-stage) and Grid views in the toolbar. The weighted pipeline total multiplies each deal's amount by the stage's close probability.",
      },
      {
        heading: "Stage Transitions",
        body: "Drag a deal card or use the stage dropdown. Moving to Won or Lost opens a reason modal whose structured data feeds the Forecast page.",
      },
      {
        heading: "Probability Override",
        body: "Set a per-deal probability override on the detail page. The override takes precedence over the stage default in the weighted total.",
      },
      {
        heading: "Forecast",
        body: "The Forecast page (admin / revenue_leader) shows gap-to-quota and weighted pipeline by owner. Quotas are set per user per quarter.",
      },
    ],
  },
  admin: {
    title: "Admin",
    sections: [
      {
        heading: "Users & Roles",
        body: "Invite users, assign roles, and deactivate accounts under Admin → Users. The role matrix is at Admin → Roles.",
      },
      {
        heading: "Importing Data",
        body: "Admin → Import walks you through CSV upload, column mapping, dry-run preview, and commit for Contacts, Organizations, and Deals.",
      },
      {
        heading: "Integrations",
        body: "Admin → Integrations manages app-wide OAuth approvals (Microsoft 365, Google Workspace). Per-user mailbox/calendar connections live at /me/integrations.",
      },
      {
        heading: "Teams Webhooks",
        body: "Admin → Teams Webhooks maps event classes (deal won, deal lost, @mention, etc.) to Teams channel URLs for routing notifications.",
      },
      {
        heading: "Duplicates",
        body: "Auto-detected duplicates surface under Admin → Duplicates. Review and merge Contacts or Organizations; the merge is audited and reversible.",
      },
    ],
  },
  agents: {
    title: "Agents",
    sections: [
      {
        heading: "What agents can do",
        body: "Agents run multi-step workflows on your behalf — searching records, drafting emails, scoring leads, updating deals, and more. Each agent has a set of approved skills and policy guardrails.",
      },
      {
        heading: "Running an agent",
        body: "Open the ChatRail, type a request, and send. The agent responds in the conversation. For long-running work, a Run card appears in the run list.",
      },
      {
        heading: "Approvals",
        body: "Agents requiring human sign-off pause and create an Approval card. Approve or reject from the Approvals page or from within the conversation.",
      },
      {
        heading: "Schedules",
        body: "On an agent's detail page, go to Schedules to add a cron-based recurring run. Schedules can be paused individually.",
      },
    ],
  },
};

function topicForPath(pathname: string): HelpTopic {
  if (pathname.startsWith("/contacts")) return TOPICS.contacts;
  if (pathname.startsWith("/organizations")) return TOPICS.organizations;
  if (pathname.startsWith("/deals")) return TOPICS.deals;
  if (pathname.startsWith("/admin")) return TOPICS.admin;
  if (
    pathname.startsWith("/agents") ||
    pathname.startsWith("/runs") ||
    pathname.startsWith("/approvals") ||
    pathname.startsWith("/templates")
  )
    return TOPICS.agents;
  // Default: getting started
  return {
    title: "Getting Started",
    sections: [
      {
        heading: "Navigation",
        body: "The LeftNav gives you access to every section. Main areas: Contacts, Organizations, Deals, Activities, Partners. Admin-only: Agentic platform, Marketing, Admin settings.",
      },
      {
        heading: "Using the Agent",
        body: "Open the ChatRail on the right to talk to the Manna agent. You can ask it to look up records, run workflows, or explain any feature.",
      },
      {
        heading: "Keyboard Shortcuts",
        body: "Press ? to see the full shortcut map. Key bindings: g d → Deals, g c → Contacts, n d → New Deal, Cmd/Ctrl+K → Command Palette.",
      },
      {
        heading: "Importing Data",
        body: "Admin → Import walks you through CSV upload and column mapping for Contacts, Organizations, and Deals.",
      },
    ],
  };
}

// ── Drawer body ───────────────────────────────────────────────────────────────

const DRAWER_WIDTH = 380;

interface HelpDrawerBodyProps {
  onClose: () => void;
  initialQuery: string;
}

function HelpDrawerBody({ onClose, initialQuery }: HelpDrawerBodyProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const { setCollapsed: setChatCollapsed } = useChatRail();

  const topic = useMemo(() => topicForPath(location.pathname), [location.pathname]);

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return topic.sections;
    const q = searchQuery.toLowerCase();
    return topic.sections.filter(
      (s) => s.heading.toLowerCase().includes(q) || s.body.toLowerCase().includes(q),
    );
  }, [topic, searchQuery]);

  const handleAskAgent = useCallback(() => {
    setChatCollapsed(false);
    onClose();
  }, [setChatCollapsed, onClose]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Help
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close help drawer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Search */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search help topics…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          autoFocus={Boolean(initialQuery)}
        />
      </Box>

      {/* Ask the agent CTA */}
      {searchQuery.trim() && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChatIcon />}
            fullWidth
            onClick={handleAskAgent}
          >
            Ask the agent: "{searchQuery}"
          </Button>
        </Box>
      )}

      <Divider />

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        <Typography variant="overline" color="text.secondary" display="block" mb={1}>
          {topic.title}
        </Typography>

        {filteredSections.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No topics match "{searchQuery}".
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {filteredSections.map((s) => (
              <Box key={s.heading}>
                <Typography variant="subtitle2" gutterBottom>
                  {s.heading}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ChatIcon />}
          onClick={handleAskAgent}
          fullWidth
        >
          Open agent chat for deeper help
        </Button>
      </Box>
    </Box>
  );
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function HelpDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");

  const openDrawer = useCallback((query = "") => {
    setInitialQuery(query);
    setOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setOpen(false);
    setInitialQuery("");
  }, []);

  const ctx = useMemo<HelpDrawerCtx>(
    () => ({ open, openDrawer, closeDrawer }),
    [open, openDrawer, closeDrawer],
  );

  return (
    <HelpDrawerContext.Provider value={ctx}>
      {children}
      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        variant="temporary"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 2,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        <HelpDrawerBody onClose={closeDrawer} initialQuery={initialQuery} />
      </Drawer>
    </HelpDrawerContext.Provider>
  );
}

// ── HelpButton — TopBar ? icon ────────────────────────────────────────────────

export function HelpButton() {
  const { openDrawer } = useHelpDrawer();
  return (
    <Tooltip title="Help (?)">
      <IconButton
        color="inherit"
        size="small"
        onClick={() => openDrawer()}
        aria-label="Open help drawer"
      >
        <HelpOutlineIcon />
      </IconButton>
    </Tooltip>
  );
}
